<script setup lang="ts">
import { useGetMe } from "@/auth/useMe";
import { useGetCanvassHouse } from "@/canvasses/canvassHousesCrud";
import type { Canvass } from "@/canvasses/types";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import {
  useCreateConversation,
  useNewConversation,
} from "@/conversations/conversationsCrud";
import { useConversationPermissions } from "@/conversations/useConversationPermissions";
import { useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import CanvassSatisfiedTargetsForm from "./CanvassSatisfiedTargetsForm.vue";
import ConversationGuideAccordion from "./ConversationGuideAccordion.vue";
import ViewHeader from "./ViewHeader.vue";

const { canvass, houseId } = defineProps<{
  canvass: Canvass;
  houseId: number;
}>();

const router = useRouter();
const { conversation } = useNewConversation(houseId, canvass.id);
const { canvassHouse, getCanvassHouseStatus } = useGetCanvassHouse(
  canvass.id,
  houseId,
);
const { me } = useGetMe();
const { canSetContent } = useConversationPermissions(me);

const dialog = useTemplateRef("dialog");

const goToConversations = () => {
  router.push({ name: "canvassFlowHouseConversations" });
};

const goToNewContact = () => {
  router.push({
    name: "canvassFlowNewContact",
    query: { linkToConversation: conversation.value.id },
  });
};

const { createConversation, createConversationStatus, error } =
  useCreateConversation();

const saveAndContinue = (onSaved: () => void) => {
  dialog.value?.close();
  createConversation(conversation.value, { onSuccess: onSaved });
};

const handleSubmit = () => {
  if (me.value?.role === "Supporter") {
    saveAndContinue(goToConversations);
    return;
  }
  dialog.value?.showModal();
};
</script>

<template>
  <HandleQueryStatus :query-status="getCanvassHouseStatus">
    <template v-if="canvassHouse !== undefined">
      <ViewHeader
        :redirect-on-close-target="{ name: 'canvassFlowHouseConversations' }"
      >
        Neues Gespräch anlegen
        <template #subheading>
          {{ canvassHouse.street }} {{ canvassHouse.houseNumber }}
        </template>
      </ViewHeader>
      <div class="column container container-md">
        <div class="box">
          <ConversationGuideAccordion
            v-if="canvass.conversationGuide"
            :conversation-guide="canvass.conversationGuide"
          />
        </div>
        <form class="box column" @submit.prevent="handleSubmit">
          <CanvassSatisfiedTargetsForm
            v-if="canvass.canvassTargetsWithProgress.length > 0"
            v-model:satisfied-canvass-target-ids="
              conversation.satisfiedCanvassTargetIds
            "
            :canvass-targets="canvass.canvassTargetsWithProgress"
          />
          <div v-if="canSetContent">
            <label for="conversationContent" class="visually-hidden"
              >Gesprächsinhalt</label
            >
            <textarea
              id="conversationContent"
              v-model.trim="conversation.content"
              rows="8"
              placeholder="Gesprächsinhalt..."
            ></textarea>
          </div>
          <small v-if="error !== null" class="error">
            {{ error.message }}
          </small>
          <div class="row row-end">
            <RouterLink :to="{ name: 'canvassFlowHouseConversations' }"
              >Abbrechen</RouterLink
            >
            <button
              class="button"
              type="submit"
              :aria-busy="createConversationStatus === 'pending'"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </template>
  </HandleQueryStatus>

  <dialog ref="dialog">
    <p>
      Hat die Person, mit der du sprichst, Interesse ihren Kontakt zu teilen?
    </p>
    <footer class="row row-end">
      <button
        class="button button--secondary"
        @click="saveAndContinue(goToConversations)"
      >
        Nein
      </button>
      <button class="button" @click="saveAndContinue(goToNewContact)">
        Ja, Kontakt anlegen
      </button>
    </footer>
  </dialog>
</template>
